import { IWarehouseListAddress } from "@/api_framework/api_modals/Configuration";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import Input from "@/app/components/ui/Inputs/Input/index";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useClinicDetailsAPI } from "@/app/hooks/api_hooks/clinic/useClinicDetailsAPI";
import { useConfigurationCityListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationCityListAPI";
import { useConfigurationClinicListTableAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationClinicListTableAPI";
import { useWareHouseDetailsAPI } from "@/app/hooks/api_hooks/configuration/WareHouse/useWareHouseDetailsAPI";
import { useWareHouseListAPI } from "@/app/hooks/api_hooks/configuration/WareHouse/useWareHouseListAPI";
import { useWareHouseOnboadAPI } from "@/app/hooks/api_hooks/configuration/WareHouse/useWareHouseOnboadAPI";
import { useWareHouseUpdateAPI } from "@/app/hooks/api_hooks/configuration/WareHouse/useWareHouseUpdateAPI";
import useFormValidations from "@/app/hooks/useFormValidations";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IConfigurationListState } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { Switch } from "@headlessui/react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import clsx from "clsx";
const FIELD_INPUT_CLASS = "h-[38px] rounded-lg";

export interface IWareHouseFields {
  warehouse_name: string;
  warehouse_display_name: string;
  alias_code: string;
  clinic_id: string;
  contact_no: string;
  email: string;
  address: string;
  address2: string;
  pincode: string;
  city: string;
  state: string;
  gstn: string;
  latitude: string;
  longitude: string;
  source_warehouse_id?: string;
  status?: boolean;
}
export interface IWareHouseFieldsProps {
  name: string;
  display_name: string;
  alias_code?: string | null;
  clinic_id: string;
  contact_no: string;
  email_id: string;
  address_details?: IWarehouseListAddress | null;
  gstn: string;
  latitude: number;
  longitude: number;
  source_warehouse_id?: string;
  status?: string;
}

const WarehouseAddComponent = () => {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("CONFIGURATION", "id");
  const { routeState } = useRouteInfo(routeId);
  const { clinicListTable, cityList, wareHouseList } = routeState.state as IConfigurationListState;

  const {
    forMobile,
    forEmail,
    forOnlyNumber,
    forOnlyAlphabetWithoutDotSpace,
    forAlphaNumericWithGST,
    forAllAcceptWithOutStartWithDotAndSpace,
    forOnlyAlphabetWithAlfhanumeric,
    forLatitude,
    forLongitude,
  } = useFormValidations();

  const [params, _setparams] = useSearchParams();
  const { execute: fetcWareHouseDetailsAPI, wareHouseDetails } = useWareHouseDetailsAPI();
  const { execute: createWareHouse } = useWareHouseOnboadAPI();
  const { execute: updateWireHouse } = useWareHouseUpdateAPI();
  const { execute: fetchWareHouseList } = useWareHouseListAPI();
  const fetchClinicList = useConfigurationClinicListTableAPI();
  const fetchCityList = useConfigurationCityListAPI();
  const { execute: fetchClinicDetails, clinicAddess } = useClinicDetailsAPI();
  const clinicList = useSelectMenuReducer(clinicListTable, "display_name", "id");
  const cityListItem = useSelectMenuReducer(cityList, "name", "id");
  const wareHouseListItems = useSelectMenuReducer(wareHouseList, "warehouse_display_name", "warehouse_id");

  const formHook = useForm<IWareHouseFields>({
    mode: "onChange",
    defaultValues: {
      status: true,
    },
  });

  useEffect(() => {
    const wareHouseId = params.get("warehouse_id");
    if (wareHouseId) {
      fetcWareHouseDetailsAPI(wareHouseId);
    }
  }, []);

  useEffect(() => {
    if (!wareHouseDetails) return;
    if (wareHouseDetails) {
      formHook.setValue("address", wareHouseDetails?.address_details?.add_line_1);
      formHook.setValue("address2", wareHouseDetails?.address_details?.add_line_2);
      formHook.setValue("city", wareHouseDetails?.address_details?.city);
      formHook.setValue("clinic_id", wareHouseDetails?.clinic_id);
      formHook.setValue("email", wareHouseDetails?.email_id);
      formHook.setValue("gstn", wareHouseDetails?.gstn);
      formHook.setValue("contact_no", wareHouseDetails?.contact_no);
      formHook.setValue("pincode", wareHouseDetails?.address_details?.pin_code);
      formHook.setValue("state", wareHouseDetails?.address_details?.state);
      formHook.setValue("warehouse_display_name", wareHouseDetails?.display_name);
      formHook.setValue("warehouse_name", wareHouseDetails?.name);
      formHook.setValue("alias_code", wareHouseDetails?.alias_code ?? "");
      formHook.setValue("status", wareHouseDetails?.status?.toLowerCase() === "active" ? true : false);
      formHook.setValue("latitude", wareHouseDetails?.latitude?.toString() ?? "");
      formHook.setValue("longitude", wareHouseDetails?.longitude?.toString() ?? "");
    }
  }, [wareHouseDetails]);

  useEffect(() => {
    fetchClinicList();
    fetchCityList();
    fetchWareHouseList();
  }, []);

  useEffect(() => {
    const clinic_id = formHook.getValues("clinic_id");
    if (clinic_id) {
      fetchClinicDetails(clinic_id);
    }
  }, [formHook.watch("clinic_id")]);

  useEffect(() => {
    if (clinicAddess) {
      formHook.setValue("address", clinicAddess?.address);
      formHook.setValue("address2", clinicAddess?.address2);
      formHook.setValue("city", clinicAddess?.city_name);
      formHook.setValue("contact_no", clinicAddess?.mobile);
      formHook.setValue("pincode", clinicAddess?.pincode);
      formHook.setValue("state", clinicAddess?.state_name);
      // On edit the alias code comes from the warehouse details and must not
      // be re-derived from the clinic; the BE ignores it on update anyway.
      if (!wareHouseDetails) {
        formHook.setValue("alias_code", clinicAddess?.alias_code ? `T${clinicAddess.alias_code}` : "");
      }
    }
  }, [clinicAddess, wareHouseDetails]);

  const onSubmit = (data: IWareHouseFields) => {
    const constructedData: IWareHouseFieldsProps = {
      address_details: {
        add_line_1: data.address,
        add_line_2: data.address2 ? data.address2 : null,
        city: data.city,
        state: data.state,
        pin_code: data.pincode,
      },
      clinic_id: data.clinic_id,
      email_id: data.email,
      gstn: data.gstn ? data.gstn : null,
      contact_no: data.contact_no,
      name: data.warehouse_name,
      display_name: data.warehouse_display_name,
      latitude: Number(data.latitude),
      longitude: Number(data.longitude),
      alias_code: data.alias_code || null,
      status: data.status ? "Active" : "InActive",
    };
    if (wareHouseDetails) {
      updateWireHouse(constructedData, wareHouseDetails?.warehouse_id).then(({ status }) => {
        if (status) {
          navigate(`/app/configuration/warehouse-setup`);
        }
      });
    } else {
      if (data.source_warehouse_id) {
        constructedData.source_warehouse_id = data.source_warehouse_id;
      }
      createWareHouse(constructedData).then(({ status }) => {
        if (status) {
          navigate(`/app/configuration/warehouse-setup`);
        }
      });
    }
  };

  const handelBackToTaxList = () => {
    navigate(`/app/configuration/warehouse-setup`);
  };

  return (
    <div className="rounded-md bg-white py-3 px-5">
      <form className="w-full mt-4" onSubmit={formHook.handleSubmit(onSubmit)}>
        <div className="font-semibold text-base text-black">Warehouse Setup</div>
        <div className="my-6 sm:my-8 flex flex-col gap-6">
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0">
            <div className="w-full shrink-0 text-sm sm:text-base sm:w-2/12">
              <p>
                Name <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-full sm:w-1/4">
              <Input
                className="text-xs"
                inputClassName={FIELD_INPUT_CLASS}
                placeholder="Enter Name"
                register={formHook.register("warehouse_name", {
                  required: true,
                  ...forAllAcceptWithOutStartWithDotAndSpace.validations,
                })}
                fieldError={formHook.formState.errors.warehouse_name}
                errorMessages={[forAllAcceptWithOutStartWithDotAndSpace.errors, { message: null, type: "required" }]}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0">
            <div className="w-full shrink-0 text-sm sm:text-base sm:w-2/12">
              <p>
                Display Name <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-full sm:w-1/4">
              <Input
                className="text-xs"
                inputClassName={FIELD_INPUT_CLASS}
                placeholder="Enter Display Name *"
                register={formHook.register("warehouse_display_name", {
                  required: true,
                  ...forAllAcceptWithOutStartWithDotAndSpace.validations,
                })}
                fieldError={formHook.formState.errors.warehouse_display_name}
                errorMessages={[forAllAcceptWithOutStartWithDotAndSpace.errors, { message: null, type: "required" }]}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0">
            <div className="w-full shrink-0 text-sm sm:text-base sm:w-2/12">
              <p>
                Clinic <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-full sm:w-1/4">
              <SearchableSelectMenu
                disabled={!!wareHouseDetails}
                errorMessages={[{ message: null, type: "required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("clinic_id", item.id);
                  }
                }}
                fieldError={formHook.formState.errors.clinic_id}
                register={formHook.register("clinic_id", { required: true })}
                selectItems={clinicList}
                placeholder="Select Clinic"
                showTooltips={true}
                showTypedErrors={true}
                showDropdownIcon={true}
                defaultSelected={clinicList?.filter((oc) => oc.id === formHook.watch("clinic_id"))[0]}
                listBoxClassName="w-full"
                className="text-gray-800"
                containerClassName="w-full"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0">
            <div className="w-full shrink-0 text-sm sm:text-base sm:w-2/12">
              <p>Alias Code</p>
            </div>
            <div className="w-full sm:w-1/4">
              <Input
                className="text-xs"
                inputClassName={clsx(FIELD_INPUT_CLASS, "bg-gray-100 cursor-not-allowed")}
                placeholder="Auto-generated from clinic"
                readOnly={true}
                register={formHook.register("alias_code")}
                fieldError={formHook.formState.errors.alias_code}
                errorMessages={[]}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0">
            <div className="w-full shrink-0 text-sm sm:text-base sm:w-2/12">
              <p>
                Contact Number <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-full sm:w-1/4">
              <Input
                className="text-xs"
                inputClassName={FIELD_INPUT_CLASS}
                maxLength={10}
                placeholder="Enter Contact Number"
                register={formHook.register("contact_no", {
                  required: true,
                  ...forMobile.validations,
                })}
                fieldError={formHook.formState.errors.contact_no}
                errorMessages={[forMobile.errors, { message: null, type: "required" }]}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0">
            <div className="w-full shrink-0 text-sm sm:text-base sm:w-2/12">
              <p>
                Email ID <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-full sm:w-1/4">
              <Input
                className="text-xs"
                inputClassName={FIELD_INPUT_CLASS}
                placeholder="Enter Email Id"
                register={formHook.register("email", {
                  required: true,
                  ...forEmail.validations,
                })}
                fieldError={formHook.formState.errors.email}
                errorMessages={[forEmail.errors, { message: null, type: "required" }]}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:gap-0">
            <div className="w-full shrink-0 text-sm sm:text-base sm:w-2/12">
              <p>
                Address <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-full sm:w-1/4 flex flex-col gap-4">
              <Input
                className="text-xs"
                inputClassName={FIELD_INPUT_CLASS}
                placeholder="Enter First Address"
                register={formHook.register("address", {
                  required: true,
                  ...forOnlyAlphabetWithAlfhanumeric.validations,
                })}
                fieldError={formHook.formState.errors.address}
                errorMessages={[forOnlyAlphabetWithAlfhanumeric.errors, { message: null, type: "required" }]}
              />
              <Input
                className="text-xs"
                inputClassName={FIELD_INPUT_CLASS}
                placeholder="Enter Second Address"
                register={formHook.register("address2", {
                  required: false,
                  ...forOnlyAlphabetWithAlfhanumeric.validations,
                })}
                fieldError={formHook.formState.errors.address2}
                errorMessages={[forOnlyAlphabetWithAlfhanumeric.errors, { message: null, type: "required" }]}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
                <SearchableSelectMenu
                  errorMessages={[{ message: null, type: "required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("city", item.title);
                    }
                  }}
                  fieldError={formHook.formState.errors.city}
                  register={formHook.register("city", { required: true })}
                  selectItems={cityListItem}
                  placeholder="Select City"
                  showTooltips={true}
                  showTypedErrors={true}
                  showDropdownIcon={true}
                  defaultSelected={cityListItem?.filter((oc) => oc.title === formHook.watch("city"))[0]}
                  listBoxClassName="w-full"
                  className="text-gray-800"
                  containerClassName="w-full"
                />
                <Input
                  className="text-xs"
                  inputClassName={FIELD_INPUT_CLASS}
                  placeholder="Enter State"
                  register={formHook.register("state", {
                    required: true,
                    ...forOnlyAlphabetWithoutDotSpace.validations,
                  })}
                  fieldError={formHook.formState.errors.state}
                  errorMessages={[forOnlyAlphabetWithoutDotSpace.errors, { message: null, type: "required" }]}
                />
              </div>
              <div className="w-full sm:w-2/4 sm:pr-1 pt-2">
                <Input
                  maxLength={6}
                  className="text-xs"
                  inputClassName={FIELD_INPUT_CLASS}
                  placeholder="Enter Pin"
                  register={formHook.register("pincode", {
                    required: true,
                    ...forOnlyNumber.validations,
                  })}
                  fieldError={formHook.formState.errors.pincode}
                  errorMessages={[forOnlyNumber.errors, { message: null, type: "required" }]}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0">
            <div className="w-full shrink-0 text-sm sm:text-base sm:w-2/12">
              <p>
                GSTN <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-full sm:w-1/4">
              <Input
                className="text-xs"
                inputClassName={FIELD_INPUT_CLASS}
                placeholder="Enter GST Number"
                register={formHook.register("gstn", {
                  required: true,
                  ...forAlphaNumericWithGST.validations,
                })}
                fieldError={formHook.formState.errors.gstn}
                errorMessages={[forAlphaNumericWithGST.errors, { message: null, type: "required" }]}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0">
            <div className="w-full shrink-0 text-sm sm:text-base sm:w-2/12">
              <p>
                Latitude <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-full sm:w-1/4">
              <Input
                className="text-xs"
                inputClassName={FIELD_INPUT_CLASS}
                placeholder="Enter Latitude"
                register={formHook.register("latitude", {
                  required: true,
                  ...forLatitude.validations,
                })}
                fieldError={formHook.formState.errors.latitude}
                errorMessages={[forLatitude.errors, { message: null, type: "required" }]}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0">
            <div className="w-full shrink-0 text-sm sm:text-base sm:w-2/12">
              <p>
                Longitude <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-full sm:w-1/4">
              <Input
                className="text-xs"
                inputClassName={FIELD_INPUT_CLASS}
                placeholder="Enter Longitude"
                register={formHook.register("longitude", {
                  required: true,
                  ...forLongitude.validations,
                })}
                fieldError={formHook.formState.errors.longitude}
                errorMessages={[forLongitude.errors, { message: null, type: "required" }]}
              />
            </div>
          </div>
          {!wareHouseDetails && (
            <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0">
              <div className="w-full shrink-0 text-sm sm:text-base sm:w-2/12">
                <p>Clone From Warehouse</p>
              </div>
              <div className="w-full sm:w-1/4">
                <SearchableSelectMenu
                  fieldError={formHook.formState.errors.source_warehouse_id}
                  errorMessages={[]}
                  onSelectItem={(item) => {
                    formHook.setValue("source_warehouse_id", item ? item.id : "");
                  }}
                  register={formHook.register("source_warehouse_id")}
                  selectItems={wareHouseListItems}
                  placeholder="Select Warehouse"
                  withReset={true}
                  showTooltips={true}
                  showDropdownIcon={true}
                  defaultSelected={
                    wareHouseListItems?.filter((wh) => wh.id === formHook.watch("source_warehouse_id"))[0]
                  }
                  listBoxClassName="w-full"
                  className="text-gray-800"
                  containerClassName="w-full"
                />
              </div>
            </div>
          )}
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0">
            <div className="w-full shrink-0 text-sm sm:text-base sm:w-2/12">
              <p>
                Status <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-full sm:w-1/4">
              <Switch
                checked={formHook.watch("status")}
                onChange={() => {
                  if (formHook.watch("status")) {
                    formHook.setValue("status", false);
                  } else {
                    formHook.setValue("status", true);
                  }
                }}
                className={`${formHook.watch("status") ? "bg-[#4D9FFF]" : "bg-[#CCCCCC]"}
          relative inline-flex h-6 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75`}
              >
                <span
                  aria-hidden="true"
                  className={`${formHook.watch("status") ? "translate-x-8" : "translate-x-0"}
            pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out`}
                />
              </Switch>
            </div>
          </div>
        </div>
        <div className="flex justify-end items-center p-4 gap-4 border-t-2 sticky bottom-0 right-0 w-full bg-white z-20">
          <button type="button" onClick={() => handelBackToTaxList()} className={SECONDARY_BUTTON}>
            Cancel
          </button>
          <button type="submit" className={PRIMARY_BUTTON}>
            {wareHouseDetails ? "Update" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};
export default WarehouseAddComponent;
